import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, HostListener, inject, OnInit, signal, viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IconDirective } from '@coreui/icons-angular';
import { MatomoTrackerService } from 'analytics';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import {
  dnaOfferUrl,
  DR_FUTURE_ASSISTANTS,
  DrFutureAssistantBadgeService,
  DrFutureAssistantDef,
  isDrFutureAssistantKey
} from '../../../services/dr-future-assistants';
import {
  DrFutureAssistantsResponse,
  DrFutureErrorCode,
  DrFutureFeedbackRating,
  DrFutureMessage,
  DrFutureMode,
  DrFutureService,
  DrFutureSource,
  DrFutureThread,
  DrFutureThreadDetail
} from '../../../services/dr-future.service';
import { DrFutureAvatarComponent, drFutureAvatarSmall } from './dr-future-avatar.component';
import { DrfAnswerPipe, DrfOwnTextPipe, plainLength, shortOrigin } from './dr-future-text';

/**
 * `locked`: a DNA assistant without a finished DNA evaluation (lock notice).
 * `not_connected`: a DNA assistant whose provider is not configured yet and with
 * no history to read — otherwise the history shows and only asking is replaced.
 */
type PageState = 'loading' | 'ready' | 'disabled' | 'load_error' | 'locked' | 'not_connected';

/** Error codes of the API that switch the page state (body `code` or `error`). */
const DNA_REQUIRED = 'dna_report_required';
const NOT_CONNECTED = 'assistant_not_connected';
const MODE_NOT_AVAILABLE = 'mode_not_available';

/**
 * Dr. Future's own texts name him; for the other assistants the same text comes
 * from `dr_future.assistants.common.*` with `{{name}}`.
 */
const NAMED_TEXTS: Record<string, string> = {
  'dr_future.typing': 'dr_future.assistants.common.typing',
  'dr_future.composer.placeholder_follow_up': 'dr_future.assistants.common.placeholder_follow_up',
  'dr_future.disclaimer': 'dr_future.assistants.common.disclaimer',
  'dr_future.disclaimer_short': 'dr_future.assistants.common.disclaimer_short',
  'dr_future.slow': 'dr_future.assistants.common.slow',
  'dr_future.membership.title': 'dr_future.assistants.common.membership_title',
  'dr_future.membership.text': 'dr_future.assistants.common.membership_text',
  'dr_future.errors.temporary': 'dr_future.assistants.common.errors.temporary',
  'dr_future.errors.busy': 'dr_future.assistants.common.errors.busy',
  'dr_future.errors.unprocessable': 'dr_future.assistants.common.errors.unprocessable',
  'dr_future.errors.unavailable': 'dr_future.assistants.common.errors.unavailable',
  'dr_future.errors.pending': 'dr_future.assistants.common.errors.pending',
};

const MATOMO_CATEGORY = 'DrFuture';
const POLL_INTERVAL_MS = 2_000;
const POLL_MAX_MS = 90_000;
export const QUESTION_MAX_LENGTH = 2_000;
/** Mirrors DrFutureMessage::FEEDBACK_COMMENT_MAX_LENGTH. */
export const FEEDBACK_COMMENT_MAX_LENGTH = 2_000;
/** Conversations shown before "View all conversations" (template LIST_LIMIT). */
export const LIST_LIMIT = 5;
/**
 * While the product is in beta every bot bubble carries the marker — the greeting and
 * each answer. One switch, so it comes off everywhere at once when beta ends.
 */
export const BETA = true;

/** Known provider source types; anything else is shown humanised. */
const SOURCE_TYPES = [
  'clinical_guideline', 'treatment_summary', 'drug_label', 'consumer_health',
  'terminology', 'scientific_literature', 'practitioner'
];

/** Failure codes worth a "try again" (same request_id). Mirrors DrFutureMessage::RETRYABLE_ERROR_CODES. */
const TEMPORARY: DrFutureErrorCode[] = ['unreachable', 'timeout', 'upstream_error', 'stale'];
const UNPROCESSABLE: DrFutureErrorCode[] = ['bad_request', 'bad_response', 'protocol_mismatch'];

/** The template's four visible states; `pending` is the typing bubble instead. */
export type DrfStatus = 'answer' | 'clarify' | 'handoff' | 'error';

interface PopoverPosition { left: number; top: number; owner: HTMLElement; }

/** A conversation's mode; older backends send none, which means evidence. */
export function threadMode(thread: DrFutureThread | null | undefined): DrFutureMode {
  return thread?.mode === 'alternative' ? 'alternative' : 'evidence';
}

/**
 * Dr. Future — the digital, AI-based health-information companion of the Health
 * Club (never a doctor), in the provider's "Ask Dr. Future" design template
 * (docs/globalhealth/drfuture/website-integrated/). The template's own store is
 * replaced by our backend: the thread list comes from `listThreads`, reopening a
 * thread continues the SAME backend thread (the provider resolves its case by it),
 * and nothing about a conversation is kept in the browser.
 *
 * The backend asks the provider in a background job, so a question comes back
 * `pending` and is polled until it is answered. There is deliberately no fallback
 * to canned answers anywhere: a canned medical answer shown because the service was
 * unreachable would be indistinguishable from a real one. Failures show as failures.
 *
 * `?q=` (the dashboard hero) opens a NEW conversation with that question and
 * sends it at once; the parameter is then removed so a reload does not ask twice.
 */
@Component({
  selector: 'gh-dr-future-page',
  standalone: true,
  imports: [NgTemplateOutlet, FormsModule, RouterLink, IconDirective, TranslatePipe, DrFutureAvatarComponent, DrfAnswerPipe, DrfOwnTextPipe],
  templateUrl: './dr-future-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DrFuturePageComponent implements OnInit {
  private api = inject(DrFutureService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private i18n = inject(I18nService);
  private translate = inject(TranslateService);
  private matomo = inject(MatomoTrackerService);
  private destroyRef = inject(DestroyRef);
  private badges = inject(DrFutureAssistantBadgeService);

  private composerRef = viewChild<ElementRef<HTMLTextAreaElement>>('composer');
  private scrollRef = viewChild<ElementRef<HTMLElement>>('scroller');
  private avatar = viewChild(DrFutureAvatarComponent);

  readonly maxLength = QUESTION_MAX_LENGTH;
  readonly feedbackMaxLength = FEEDBACK_COMMENT_MAX_LENGTH;
  readonly beta = BETA;
  readonly avatarSmall = drFutureAvatarSmall;

  /** Which assistant this page talks to (route data `assistant`, default Dr. Future). */
  readonly assistant: DrFutureAssistantDef = (() => {
    const key = this.route.snapshot.data['assistant'];
    return isDrFutureAssistantKey(key) ? DR_FUTURE_ASSISTANTS[key] : DR_FUTURE_ASSISTANTS.dr_future;
  })();
  readonly isDrFuture = this.assistant.key === 'dr_future';
  /** `{{name}}` for every assistant-neutral text. */
  readonly nameParams = { name: this.assistant.name };
  /** The static DNA offer page on this host (no language prefix). */
  readonly dnaOfferUrl = dnaOfferUrl();

  readonly state = signal<PageState>('loading');
  /** DNA evaluation state behind the `locked` notice. */
  readonly dnaStatus = signal<'none' | 'pending'>('none');
  /** The assistant's provider is configured (false = history readable, asking refused). */
  readonly connected = signal(true);
  readonly canAsk = signal(true);
  readonly threads = signal<DrFutureThread[]>([]);
  readonly activeThread = signal<DrFutureThread | null>(null);
  readonly messages = signal<DrFutureMessage[]>([]);
  readonly loadingThread = signal(false);

  // ── Answer mode (Dr. Future only) ───────────────────────────────────────────
  /**
   * The selected mode. A conversation's mode is fixed when it is created and never
   * mixed: opening a conversation restores ITS mode (switch, avatar, header, pill and
   * accent all follow the conversation), and switching away from a started conversation
   * of the other mode deselects it.
   */
  readonly mode = signal<DrFutureMode>('evidence');
  /**
   * The backend accepts `mode: 'alternative'`. This flag is the ONLY runtime difference
   * between "holistic coming soon" and "holistic live" — no UI rebuild when it flips.
   */
  readonly alternativeModeEnabled = signal(false);

  // ── Conversation list ──────────────────────────────────────────────────────
  readonly viewAll = signal(false);
  /** Narrow panes: the conversation list as an overlay. */
  readonly convsOpen = signal(false);
  readonly shownThreads = computed(() => this.viewAll() ? this.threads() : this.threads().slice(0, LIST_LIMIT));
  readonly hasMoreThreads = computed(() => this.threads().length > LIST_LIMIT);

  readonly question = signal('');
  readonly sending = signal(false);
  readonly sendError = signal<string | null>(null);
  /** Polling ran past POLL_MAX_MS without a result. */
  readonly slow = signal(false);

  readonly pending = computed(() => this.messages().some(m => m.role === 'assistant' && m.status === 'pending'));
  /** A question is in flight: no mode switch, no new conversation meanwhile. */
  readonly busy = computed(() => this.sending() || this.pending());

  /**
   * Whether this conversation may be asked anything at all. The alternative branch has
   * no knowledge base yet, and a service that ignores the mode would return an
   * evidence-based answer under a holistic label — quietly breaking the promise that
   * the modes are never mixed. So the composer is closed, and `send()` refuses at the
   * entry point too, not only visually.
   */
  readonly composerOpen = computed(() => {
    if (!this.isDrFuture) return true;
    const thread = this.activeThread();
    const mode = thread ? threadMode(thread) : this.mode();
    return mode !== 'alternative' || this.alternativeModeEnabled();
  });

  readonly canSend = computed(() => {
    const q = this.question().trim();
    return this.composerOpen() && this.canAsk() && this.connected() && !this.busy()
      && q.length > 0 && q.length <= this.maxLength && !this.activeThread()?.archived;
  });

  /** The mode the visible conversation speaks in (avatars of its bubbles, lead line). */
  readonly conversationMode = computed<DrFutureMode>(() => {
    if (!this.isDrFuture) return 'evidence';
    const thread = this.activeThread();
    return thread ? threadMode(thread) : this.mode();
  });

  // ── Sources ────────────────────────────────────────────────────────────────
  /** Answers whose source list is expanded (collapsed by default, as in the template). */
  readonly sourcesOpen = signal<ReadonlySet<number>>(new Set());
  /** `<messageId>-<n>` of the source a citation just pointed at. */
  readonly flashedSource = signal<string | null>(null);

  // ── Feedback ───────────────────────────────────────────────────────────────
  // The rating is ours the moment the backend stored it; whether it reached the
  // provider is not the member's concern (only a quiet note when the sync failed).
  /** The answer whose comment field is open. */
  readonly feedbackOpenFor = signal<number | null>(null);
  readonly feedbackDraft = signal('');
  /** The answer whose rating/comment is being saved right now. */
  readonly feedbackSaving = signal<number | null>(null);
  /** Answers that show "sent" (after Send/Skip). */
  readonly feedbackThanked = signal<ReadonlySet<number>>(new Set());
  /** Answers whose last save request failed (network/HTTP) — distinct from a failed provider sync. */
  readonly feedbackSaveError = signal<number | null>(null);

  /** "About answer modes" popover, positioned under the button that opened it. */
  readonly popover = signal<PopoverPosition | null>(null);

  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollStartedAt = 0;

  constructor() {
    this.destroyRef.onDestroy(() => this.stopPolling());
  }

  get lang(): string { return this.i18n.getLanguage(); }

  ngOnInit(): void {
    this.state.set('loading');
    // The backend is the authority for the lock, not the cached user payload:
    // ask it first. An older backend without the endpoint still serves Dr. Future.
    this.api.assistants().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => this.applyAssistants(res),
      error: (err: HttpErrorResponse) => {
        if (this.handleGateError(err)) return;
        if (this.isDrFuture) { this.loadThreads(); return; }
        this.state.set(this.isDisabled(err) ? 'disabled' : 'load_error');
      }
    });
  }

  private applyAssistants(res: DrFutureAssistantsResponse): void {
    this.badges.apply(res.dna_report_status);
    this.alternativeModeEnabled.set(res.alternative_mode_enabled === true);
    const mine = res.assistants.find(a => a.key === this.assistant.key);
    if (mine && this.assistant.dnaGated && mine.locked_reason === DNA_REQUIRED) {
      this.showLocked(res.dna_report_status);
      return;
    }
    if (mine && !mine.connected) {
      // Dr. Future keeps its own "being prepared" page.
      if (this.isDrFuture) { this.state.set('disabled'); return; }
      this.connected.set(false);
    }
    this.loadThreads();
  }

  private loadThreads(): void {
    const initialQuestion = (this.route.snapshot.queryParamMap.get('q') ?? '').trim().slice(0, this.maxLength);
    this.api.listThreads(this.assistant.key).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.threads.set(res.threads);
        this.canAsk.set(res.can_ask);
        if (res.alternative_mode_enabled !== undefined) this.alternativeModeEnabled.set(res.alternative_mode_enabled === true);
        // Not connected and nothing to read: the notice is the whole page.
        if (!this.connected() && res.threads.length === 0) { this.state.set('not_connected'); return; }
        this.state.set('ready');
        if (initialQuestion) {
          // Drop ?q= first: a reload must not send the question a second time.
          this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
          this.newConversation();
          this.question.set(initialQuestion);
          if (this.canAsk() && this.connected()) this.send();
        }
      },
      error: (err: HttpErrorResponse) => {
        if (this.handleGateError(err)) return;
        this.state.set(this.isDisabled(err) ? 'disabled' : 'load_error');
      }
    });
  }

  private showLocked(status: string | null | undefined): void {
    this.stopPolling();
    this.dnaStatus.set(status === 'pending' ? 'pending' : 'none');
    this.state.set('locked');
  }

  /**
   * The backend's gates, from any call: a DNA lock (re-check the assistants — the
   * evaluation may have changed since this page opened) or a provider that is not
   * connected. Returns true when the error was one of them.
   */
  private handleGateError(err: HttpErrorResponse): boolean {
    const code = this.errorCode(err);
    if (err.status === 403 && code === DNA_REQUIRED) {
      this.showLocked(err.error?.dna_report_status);
      this.badges.apply(err.error?.dna_report_status === 'pending' ? 'pending' : 'none');
      return true;
    }
    if (err.status === 503 && code === NOT_CONNECTED) {
      this.connected.set(false);
      if (this.state() !== 'ready' || this.threads().length === 0) this.state.set('not_connected');
      return true;
    }
    return false;
  }

  private errorCode(err: HttpErrorResponse): string | null {
    const body = err.error;
    if (!body || typeof body !== 'object') return null;
    return body.code ?? body.error ?? null;
  }

  /** Dr. Future's own key, or the assistant-neutral one for the other assistants. */
  named(key: string): string {
    return this.isDrFuture ? key : (NAMED_TEXTS[key] ?? key);
  }

  // ── Mode ───────────────────────────────────────────────────────────────────

  setMode(mode: DrFutureMode): void {
    if (!this.isDrFuture || this.busy() || mode === this.mode()) return;
    this.mode.set(mode);
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'mode_switch', name: mode });
    // Modes are never mixed: a started conversation stays in its mode, so switching
    // away from it leaves it and starts a new, not yet asked one.
    const thread = this.activeThread();
    if (thread && threadMode(thread) !== mode) this.deselect();
  }

  // ── Threads ────────────────────────────────────────────────────────────────

  newConversation(): void {
    if (this.sending()) return;
    this.deselect();
    this.convsOpen.set(false);
    this.focusComposer();
  }

  /** Back to a new, not yet started conversation in the current mode. */
  private deselect(): void {
    this.stopPolling();
    this.resetFeedbackUi();
    this.activeThread.set(null);
    this.messages.set([]);
    this.sendError.set(null);
    this.slow.set(false);
    this.avatar()?.idle();
  }

  openThread(thread: DrFutureThread): void {
    if (this.sending()) return;
    this.stopPolling();
    this.resetFeedbackUi();
    this.sendError.set(null);
    this.slow.set(false);
    this.activeThread.set(thread);
    // The conversation decides the mode, not the other way round.
    if (this.isDrFuture) this.mode.set(threadMode(thread));
    this.messages.set([]);
    this.convsOpen.set(false);
    this.loadingThread.set(true);
    this.api.getThread(thread.id, this.lang).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.loadingThread.set(false);
        this.applyDetail(res);
        if (this.pending()) {
          this.avatar()?.think();
          this.startPolling();
        } else {
          this.avatar()?.idle();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.loadingThread.set(false);
        if (this.handleGateError(err)) return;
        this.sendError.set(this.httpErrorText(err));
      }
    });
  }

  onThreadKeydown(event: KeyboardEvent, thread: DrFutureThread): void {
    if (event.target !== event.currentTarget) return; // the archive button handles its own keys
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openThread(thread);
    }
  }

  /** Threads are never deleted: the template's "remove" is our archive. */
  archive(thread: DrFutureThread, event?: Event): void {
    event?.stopPropagation();
    if (this.busy() && this.activeThread()?.id === thread.id) return;
    this.api.update(thread.id, { archived: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.threads.update(list => list.filter(t => t.id !== thread.id));
        if (this.activeThread()?.id === thread.id) this.deselect();
      },
      error: (err: HttpErrorResponse) => {
        if (this.handleGateError(err)) return;
        this.sendError.set(this.httpErrorText(err));
      }
    });
  }

  toggleViewAll(): void {
    this.viewAll.update(v => !v);
  }

  toggleConvs(): void {
    this.convsOpen.update(v => !v);
  }

  // ── Asking ─────────────────────────────────────────────────────────────────

  onComposerKeydown(event: KeyboardEvent): void {
    // Enter sends, Shift+Enter is a new line; never while an IME composes a word.
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.send();
    }
  }

  onQuestionInput(value: string): void {
    this.question.set(value);
    this.autoGrow();
  }

  send(): void {
    // The entry point refuses, not only the button: see `composerOpen`.
    if (!this.composerOpen() || !this.canSend()) return;
    const question = this.question().trim();
    this.sending.set(true);
    this.sendError.set(null);
    this.slow.set(false);

    const thread = this.activeThread();
    const mode = this.isDrFuture ? this.mode() : 'evidence';
    const request$ = thread
      ? this.api.ask(thread.id, question, this.lang)
      : this.api.createThread(this.assistant.key, question, this.lang, mode);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.sending.set(false);
        this.question.set('');
        this.autoGrow();
        this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'question_sent', name: thread ? 'follow_up' : 'new_thread' });
        this.activeThread.set(res.thread);
        this.messages.update(list => [...list, ...res.messages]);
        this.upsertThread(res.thread);
        this.scrollToEnd();
        // "think" while the answer is on its way; "talk" once there is something to read.
        this.avatar()?.think();
        this.startPolling();
      },
      error: (err: HttpErrorResponse) => {
        this.sending.set(false);
        if (this.handleGateError(err)) return;
        if (err.status === 503 && this.errorCode(err) === MODE_NOT_AVAILABLE) {
          // The backend says holistic is not live (any more): close the composer again.
          this.alternativeModeEnabled.set(false);
          this.sendError.set(this.translate.translate('dr_future.errors.mode_not_available'));
          return;
        }
        if (err.status === 403) this.canAsk.set(false);
        if (this.isDisabled(err)) { this.state.set('disabled'); return; }
        this.sendError.set(this.httpErrorText(err));
      }
    });
  }

  retry(message: DrFutureMessage): void {
    this.sendError.set(null);
    this.slow.set(false);
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'retry', name: message.error_code ?? message.status });
    this.api.retry(message.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.messages.update(list => list.map(m => (m.id === res.message.id ? res.message : m)));
        this.upsertThread(res.thread);
        this.avatar()?.think();
        this.startPolling();
      },
      error: (err: HttpErrorResponse) => {
        if (this.handleGateError(err)) return;
        this.sendError.set(this.httpErrorText(err));
      }
    });
  }

  /** The provider said `error` → always worth another go; our `failed` only when temporary. */
  canRetry(message: DrFutureMessage): boolean {
    return message.status === 'error' || !!message.retryable;
  }

  // ── Sources ────────────────────────────────────────────────────────────────

  sourcesExpanded(message: DrFutureMessage): boolean {
    return this.sourcesOpen().has(message.id);
  }

  toggleSources(message: DrFutureMessage): void {
    this.sourcesOpen.update(set => {
      const next = new Set(set);
      if (next.has(message.id)) next.delete(message.id); else next.add(message.id);
      return next;
    });
  }

  /** `[n]` in an answer: open its sources and flash `sources[n-1]`. */
  cite(message: DrFutureMessage, n: number): void {
    this.sourcesOpen.update(set => new Set(set).add(message.id));
    const key = `${message.id}-${n}`;
    this.flashedSource.set(key);
    setTimeout(() => document.getElementById(`drf-src-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 0);
    setTimeout(() => { if (this.flashedSource() === key) this.flashedSource.set(null); }, 1400);
  }

  // ── Feedback ───────────────────────────────────────────────────────────────

  /**
   * A thumb was pressed: store the rating at once (keeping a comment given before),
   * then offer the optional comment field. Pressing the other thumb later re-sends.
   */
  rate(message: DrFutureMessage, rating: DrFutureFeedbackRating): void {
    if (!message.feedback_allowed || this.feedbackSaving() === message.id) return;
    if (message.feedback?.rating === rating) {
      // Same thumb again: nothing to re-send, just (re)open the comment field.
      this.openFeedbackComment(message);
      return;
    }
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'feedback', name: rating });
    this.saveFeedback(message, rating, message.feedback?.comment ?? null, () => this.openFeedbackComment(message));
  }

  submitFeedbackComment(message: DrFutureMessage): void {
    const rating = message.feedback?.rating;
    if (!rating) return;
    const comment = this.feedbackDraft().trim().slice(0, this.feedbackMaxLength);
    this.saveFeedback(message, rating, comment || null, () => this.closeFeedbackComment(message));
  }

  skipFeedbackComment(message: DrFutureMessage): void {
    this.closeFeedbackComment(message);
  }

  feedbackThankedFor(message: DrFutureMessage): boolean {
    return this.feedbackThanked().has(message.id);
  }

  private openFeedbackComment(message: DrFutureMessage): void {
    this.feedbackDraft.set(message.feedback?.comment ?? '');
    this.feedbackOpenFor.set(message.id);
    this.feedbackThanked.update(set => { const next = new Set(set); next.delete(message.id); return next; });
  }

  private closeFeedbackComment(message: DrFutureMessage): void {
    this.feedbackOpenFor.set(null);
    this.feedbackDraft.set('');
    this.feedbackThanked.update(set => new Set(set).add(message.id));
  }

  private saveFeedback(message: DrFutureMessage, rating: DrFutureFeedbackRating, comment: string | null, done: () => void): void {
    this.feedbackSaving.set(message.id);
    this.feedbackSaveError.set(null);
    this.api.feedback(message.id, rating, comment).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.feedbackSaving.set(null);
        this.messages.update(list => list.map(m => (m.id === res.message.id ? res.message : m)));
        done();
      },
      error: (err: HttpErrorResponse) => {
        this.feedbackSaving.set(null);
        if (this.handleGateError(err)) return;
        this.feedbackSaveError.set(message.id);
      }
    });
  }

  /** "Taking longer": look again (the backend gives up on its own after a few minutes). */
  checkAgain(): void {
    this.slow.set(false);
    this.avatar()?.think();
    this.startPolling();
  }

  handoff(message: DrFutureMessage): void {
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'handoff_clicked' });
    const question = this.questionFor(message)?.content ?? '';
    // The backend's handoff_url is the same target; built here so the language
    // always follows the one the member is using right now.
    this.router.navigate(['/', this.lang, 'health-concierge'], { queryParams: question ? { new: 1, q: question } : { new: 1 } });
  }

  // ── Popover "About answer modes" ───────────────────────────────────────────

  toggleModeInfo(event: MouseEvent): void {
    const owner = event.currentTarget as HTMLElement;
    const open = this.popover();
    if (open && open.owner === owner) { this.popover.set(null); return; }
    const r = owner.getBoundingClientRect();
    const w = Math.min(320, window.innerWidth - 16);
    this.popover.set({
      owner,
      left: Math.max(8, Math.min(window.innerWidth - w - 8, r.left + r.width / 2 - w / 2)),
      top: r.bottom + 8
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.popover()) return;
    const target = event.target as Element | null;
    if (target?.closest('[data-drf-popover]') || target?.closest('.drf-popover')) return;
    this.popover.set(null);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.popover.set(null);
    this.convsOpen.set(false);
  }

  // ── Polling ────────────────────────────────────────────────────────────────

  private resetFeedbackUi(): void {
    this.feedbackOpenFor.set(null);
    this.feedbackDraft.set('');
    this.feedbackSaving.set(null);
    this.feedbackSaveError.set(null);
    this.feedbackThanked.set(new Set());
    this.sourcesOpen.set(new Set());
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollStartedAt = Date.now();
    this.scheduleNextPoll();
  }

  private scheduleNextPoll(): void {
    this.pollTimer = setTimeout(() => this.poll(), POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  private poll(): void {
    const thread = this.activeThread();
    if (!thread) return;
    const before = new Map(this.messages().map(m => [m.id, m.status]));
    this.api.getThread(thread.id, this.lang).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        if (this.activeThread()?.id !== thread.id) return; // the member moved on
        this.applyDetail(res);
        for (const m of res.messages) {
          if (m.role === 'assistant' && before.get(m.id) === 'pending' && m.status !== 'pending') {
            this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'answer_received', name: m.status });
            this.scrollToEnd();
            if (m.status === 'answer' || m.status === 'clarify' || m.status === 'handoff') {
              this.avatar()?.talk(Math.min(14_000, Math.max(5_000, plainLength(m.content) * 45)));
            } else {
              this.avatar()?.idle();
            }
            if (m.status === 'clarify') this.focusComposer();
          }
        }
        if (!this.pending()) { this.stopPolling(); return; }
        if (Date.now() - this.pollStartedAt >= POLL_MAX_MS) { this.stopPolling(); this.slow.set(true); this.avatar()?.idle(); return; }
        this.scheduleNextPoll();
      },
      // A failed poll (flaky network) is simply tried again until the time is up.
      error: (err: HttpErrorResponse) => {
        if (err.status === 403 && this.errorCode(err) === DNA_REQUIRED) { this.handleGateError(err); return; }
        if (Date.now() - this.pollStartedAt >= POLL_MAX_MS) { this.stopPolling(); this.slow.set(true); this.avatar()?.idle(); return; }
        this.scheduleNextPoll();
      }
    });
  }

  private applyDetail(res: DrFutureThreadDetail): void {
    this.activeThread.set(res.thread);
    this.messages.set(res.messages);
    this.canAsk.set(res.can_ask);
    this.upsertThread({ ...res.thread, unread: false });
    this.scrollToEnd();
  }

  private upsertThread(thread: DrFutureThread): void {
    this.threads.update(list => [thread, ...list.filter(t => t.id !== thread.id)]
      .sort((a, b) => (b.last_message_at ?? b.created_at).localeCompare(a.last_message_at ?? a.created_at)));
  }

  // ── Template helpers ───────────────────────────────────────────────────────

  /** Clock time of a bubble, in the UI language. */
  fmtTime(iso: string | null | undefined): string {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString(this.lang, { hour: '2-digit', minute: '2-digit' });
  }

  /** List date: today → time, yesterday → "Yesterday", otherwise a short date (template `fmtWhen`). */
  fmtWhen(t: DrFutureThread): string {
    const d = new Date(t.last_message_at ?? t.created_at);
    const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const now = new Date();
    const diff = Math.round((day(now) - day(d)) / 864e5);
    if (diff === 0) return this.fmtTime(d.toISOString());
    if (diff === 1) return this.translate.translate('dr_future.when.yesterday');
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
    return d.toLocaleDateString(this.lang, opts);
  }

  threadModeOf(t: DrFutureThread): DrFutureMode {
    return threadMode(t);
  }

  questionFor(answer: DrFutureMessage): DrFutureMessage | undefined {
    return this.messages().find(m => m.role === 'user' && m.request_id === answer.in_reply_to_request_id);
  }

  /** i18n key of the explanation under a failed turn. */
  failureKey(message: DrFutureMessage): string {
    const code = message.error_code;
    if (code === 'rate_limited') return this.named('dr_future.errors.busy');
    if (code && TEMPORARY.includes(code)) return this.named('dr_future.errors.temporary');
    if (code && UNPROCESSABLE.includes(code)) return this.named('dr_future.errors.unprocessable');
    return this.named('dr_future.errors.unavailable');
  }

  /**
   * The template's four visible states. The provider's `error` and our own `failed`
   * (no usable answer at all) both show as the muted "temporary problem" with a way out.
   */
  statusOf(message: DrFutureMessage): DrfStatus {
    switch (message.status) {
      case 'clarify': return 'clarify';
      case 'handoff': return 'handoff';
      case 'error':
      case 'failed':  return 'error';
      default:        return 'answer';
    }
  }

  /** `dr_future.source_types.<type>` for the known types, otherwise the type humanised. */
  sourceTypeLabel(source: DrFutureSource): string {
    const type = source.source_type ?? '';
    if (!type) return '';
    const humanised = type.replace(/_/g, ' ');
    if (!SOURCE_TYPES.includes(type)) return humanised;
    const key = `dr_future.source_types.${type}`;
    const label = this.translate.translate(key);
    return label && label !== key ? label : humanised;
  }

  countParams(message: DrFutureMessage): { count: number } {
    return { count: message.sources.length };
  }

  sourceOrigin(source: DrFutureSource): string | null {
    return shortOrigin(source.url);
  }

  private httpErrorText(err: HttpErrorResponse): string {
    const code = (err.error && typeof err.error === 'object') ? err.error.error : null;
    switch (err.status) {
      case 403: return this.translate.translate(this.named('dr_future.membership.text'), this.nameParams);
      case 409: return this.translate.translate(code === 'thread_archived' ? 'dr_future.errors.archived' : this.named('dr_future.errors.pending'), this.nameParams);
      case 422: return this.translate.translate('dr_future.errors.question_invalid');
      case 429: return this.translate.translate('dr_future.errors.rate_limited');
      default:  return this.translate.translate('dr_future.errors.generic');
    }
  }

  private isDisabled(err: HttpErrorResponse): boolean {
    return err.status === 503 && this.errorCode(err) === 'dr_future_disabled';
  }

  private focusComposer(): void {
    setTimeout(() => this.composerRef()?.nativeElement.focus(), 0);
  }

  /** The composer grows with its text (capped by CSS max-height), as in the template. */
  private autoGrow(): void {
    setTimeout(() => {
      const el = this.composerRef()?.nativeElement;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }, 0);
  }

  private scrollToEnd(): void {
    setTimeout(() => {
      const el = this.scrollRef()?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 0);
  }
}
